import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DharmaWheel from "../components/DharmaWheel";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section className="min-h-[calc(100vh-5rem)] grid grid-cols-1 lg:grid-cols-2 bg-pearl">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-navy text-pearl relative overflow-hidden">
        <div className="absolute -left-24 -bottom-24 opacity-20">
          <DharmaWheel size={520} color="#CFA052" ringColor="#CFA052" />
        </div>
        <div className="relative">
          <p className="eyebrow text-gold">ABGN-UK · Members</p>
          <h2 className="mt-3 font-display text-5xl leading-tight">Welcome back to the sangha.</h2>
        </div>
        <blockquote className="relative italic font-display text-2xl text-gold max-w-md">
          "Cultivation of mind should be the ultimate aim of human existence."
          <span className="block mt-2 not-italic text-sm text-pearl/60">— Dr. B.R. Ambedkar</span>
        </blockquote>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={handle} className="w-full max-w-md" data-testid="login-form">
          <p className="eyebrow">Sign in</p>
          <h1 className="mt-3 font-display text-4xl text-navy">Enter the members' space.</h1>
          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 w-full py-3.5 bg-navy text-pearl hover:bg-navy-light rounded-sm font-medium disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-navy/15" /></div>
            <div className="relative flex justify-center"><span className="bg-pearl px-3 text-xs uppercase tracking-widest text-slate-500">or</span></div>
          </div>

          <button
            type="button"
            onClick={googleSignIn}
            data-testid="login-google-btn"
            className="w-full py-3 border border-navy/20 rounded-sm hover:bg-white transition-colors inline-flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            Continue with Google
          </button>

          <p className="mt-8 text-sm text-slate-600">
            No account yet?{" "}
            <Link to="/register" className="text-navy underline underline-offset-2 hover:text-saffron" data-testid="login-register-link">
              Register
            </Link>
          </p>
        </form>
      </div>
    </section>
  );
}
